import type { CallMode, IncomingCallPayload, TransferEvidence } from 'voxr-shared';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { afterRender, Component, computed, ElementRef, input, viewChild } from '@angular/core';

export type TransferInfoViewModel = {
  callMode?: CallMode;
  handoffId?: string;
  handoff?: IncomingCallPayload['handoff'];
  leadName?: string;
  leadPhone?: string;
  leadState?: string;
  campaignName?: string;
  summary?: string;
  statusLabel?: string;
};

@Component({
  selector: 'app-transfer-info',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './transfer-info.component.html',
})
export class TransferInfoComponent {
  readonly info = input<TransferInfoViewModel | undefined>();
  readonly emptyLabel = input('Lead');
  readonly compact = input(false);
  readonly callStatus = input<string>();
  readonly showHandoffDetails = computed(() => !['VAPI', '11Labs', 'VOXR'].includes(this.info()?.callMode || ''));
  readonly classificationLabel = computed(() => {
    const value = this.info()?.handoff?.classification;
    return value === 'ivr' ? 'IVR' : value ? value[0].toUpperCase() + value.slice(1) : 'Classification unavailable';
  });
  readonly classificationTone = computed(() => {
    const value = this.info()?.handoff?.classification;
    return value === 'human' ? 'success' : value === 'voicemail' ? 'info' : value ? 'warning' : 'neutral';
  });
  readonly evidence = input<TransferEvidence | undefined>();
  readonly providerCallId = input<string | undefined>();
  readonly carrierStatus = input<string | undefined>();
  readonly humanStatus = input<string | undefined>();
  readonly evidenceFields = computed(() => {
    const evidence = this.evidence(); if (!evidence) return [];
    const fields: [string, string | undefined][] = [
      ['Request', evidence.requestId], ['Attempt', evidence.attemptId], ['Reservation', evidence.reservationUuid],
      ['Expires', evidence.expiresAt], ['Prepared', evidence.attemptedAt], ['Dispatched', evidence.dispatchedAt],
      ['Provider call', evidence.providerCallId || this.providerCallId()], ['SIP Call-ID', evidence.sipCallId], ['SIP response', evidence.sipResponseCode],
      ['Reason', evidence.reason && /^[a-z0-9-]{1,80}$/.test(evidence.reason) ? evidence.reason : undefined],
    ];
    return fields.filter((field): field is [string, string] => typeof field[1] === 'string' && !!field[1]).map(([label, value]) => ({ label, value: value.slice(0, 256) }));
  });

  private readonly transcriptElement = viewChild<ElementRef<HTMLElement>>('transcript');

  constructor() {
    let lastElement: HTMLElement | undefined;
    let lastHandoffId: string | undefined;
    let lastTranscript: string | undefined;
    afterRender(() => {
      const element = this.transcriptElement()?.nativeElement;
      const info = this.info();
      if (element && (element !== lastElement || info?.handoffId !== lastHandoffId || info?.handoff?.transcript !== lastTranscript)) {
        element.scrollTop = element.scrollHeight;
      }
      lastElement = element;
      lastHandoffId = info?.handoffId;
      lastTranscript = info?.handoff?.transcript;
    });
  }

  readonly primaryLine = computed(() => {
    const info = this.info();
    return info?.leadName?.trim() || this.formatPhoneForDisplay(info?.leadPhone) || this.emptyLabel();
  });

  readonly secondaryLine = computed(() => {
    const info = this.info();
    if (!info?.leadName?.trim()) return undefined;
    return this.formatPhoneForDisplay(info?.leadPhone);
  });

  formatPhoneForDisplay(value?: string) {
    const raw = value?.toString().trim() || '';
    if (!raw) return '';
    if (raw.startsWith('client:')) return raw;
    if (/[A-Za-z]/.test(raw) || /[*#]/.test(raw)) return raw;

    const hasPlus = raw.startsWith('+');
    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    if (digits.length > 6 && digits.length < 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length > 3 && digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    return hasPlus ? `+${digits}` : digits;
  }
}
